import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home as HomeIcon, Store, GraduationCap, HeartPulse, Tractor, Users, Building2, LifeBuoy } from "lucide-react";
import Seo from "@/components/Seo";
import ProductVisual from "@/components/ProductVisual";
import VideoCard from "@/components/VideoCard";
import { PageHero, SectionHead, CTABand } from "@/components/Section";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CFG_SPECS, CFG_APPLICATIONS, CFG_FAQ, CFG_MEDIA, COMPANY } from "@/data/site";

const APP_ICONS = [HomeIcon, Store, GraduationCap, HeartPulse, Tractor, Users, Building2, LifeBuoy];

const BENEFITS = [
  { title: "Carbon-Free by Design", text: "Engineered as a clean alternative to petrol and diesel generators." },
  { title: "Hybrid Architecture", text: "Generation and storage in one system, designed for dependable output." },
  { title: "Built for Real Conditions", text: "Designed for off-grid and grid-fragile environments across Africa." },
  { title: "Scalable Platform", text: "From the 500W prototype toward a 5kVA-class system and beyond." },
  { title: "Low Operating Burden", text: "Developed to reduce dependence on fuel supply chains." },
  { title: "Locally Engineered", text: "Designed and developed in Sierra Leone, for African markets first." },
];

const ROADMAP = [
  { stage: "500W Prototype", status: "Complete", active: true },
  { stage: "Testing & Validation", status: "In Progress", active: false },
  { stage: "5kVA Hybrid System", status: "Development Target", active: false },
  { stage: "Pilot Deployments", status: "Planned with Partners", active: false },
  { stage: "Commercial Release", status: "To Be Confirmed", active: false },
];

export default function Generator() {
  const productSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Product",
      name: "BNM Carbon Free Generator",
      description:
        "Carbon-free generation and storage technology by BNM Sustainable Energy Limited. 500W prototype in development, evolving toward a 5kVA hybrid carbon-free system. Not yet commercially deployed.",
      brand: { "@type": "Organization", name: COMPANY.name },
      category: "Clean energy generation technology",
    }),
    []
  );

  return (
    <>
      <Seo
        title="BNM Carbon Free Generator — Clean Power, In Development"
        description="The BNM Carbon Free Generator: a 500W carbon-free generation prototype from Sierra Leone, evolving toward a 5kVA hybrid system. Status, benefits, applications and specifications."
        jsonLd={productSchema}
      />

      <section data-testid="cfg-hero" className="relative overflow-hidden bg-obsidian grid-lines-dark noise-overlay">
        <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 sm:pt-40 pb-16 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Reveal y={14}>
                <span className="inline-flex items-center gap-2.5 rounded-full border border-volt/25 bg-pine/60 px-4 py-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-volt animate-pulse-glow" aria-hidden="true" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-volt">
                    In Development — Prototype Phase
                  </span>
                </span>
              </Reveal>
              <MaskedLines
                lines={["The BNM", "Carbon Free", "Generator."]}
                className="mt-7 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.06] text-white text-balance"
              />
              <Reveal delay={0.4}>
                <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-400">
                  BNM's flagship clean-energy technology — a carbon-free generation and storage system engineered in
                  Sierra Leone. A 500W prototype today; a 5kVA-class hybrid system on the roadmap.
                </p>
              </Reveal>
              <Reveal delay={0.55}>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    data-testid="cfg-inquiry-cta"
                    className="inline-flex items-center gap-2 rounded-md bg-volt px-7 py-3.5 font-semibold text-obsidian transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-volt-soft hover:text-obsidian active:translate-y-0"
                  >
                    Make an Inquiry
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                  <a
                    href="#cfg-specs"
                    data-testid="cfg-specs-cta"
                    className="inline-flex items-center gap-2 rounded-md border border-white/20 px-7 py-3.5 font-semibold text-white transition-[transform,border-color,color] duration-200 hover:-translate-y-0.5 hover:border-volt/60 hover:text-volt active:translate-y-0"
                  >
                    View Specifications
                  </a>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.3} y={40}>
              <ProductVisual />
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="cfg-problem" className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <SectionHead
              eyebrow="The Problem"
              title="Conventional generators are costly, carbon-intensive and unreliable."
              lead="Across Sierra Leone and much of Africa, homes and institutions depend on fuel generators that are expensive to run, harmful to air quality and vulnerable to fuel supply disruptions."
            />
            <SectionHead
              eyebrow="BNM's Approach"
              title="A carbon-free alternative, engineered for these exact conditions."
              lead="The Carbon Free Generator pairs clean generation with integrated storage in a single hybrid platform — designed from the start for the environments where it will be used."
            />
          </div>
        </div>
      </section>

      <section data-testid="cfg-how-it-works" className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            eyebrow="How the System Works"
            title="Generate. Store. Deliver."
            lead="A conceptual overview of the system. Detailed technical documentation is shared with technical and investment partners under appropriate agreements."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {[
              { n: "01", t: "Carbon-Free Generation", d: "The core unit generates power without combustion of carbon fuels." },
              { n: "02", t: "Integrated Storage", d: "Energy is buffered in an integrated storage system for availability on demand." },
              { n: "03", t: "Stable Delivery", d: "Conditioned output powers homes, institutions and small enterprises." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <article className="h-full rounded-lg border border-slate-200 bg-mist p-8">
                  <p className="font-mono text-xs text-emerald-600">{s.n}</p>
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-slate-900">{s.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.d}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="cfg-hardware" className="relative overflow-hidden bg-obsidian noise-overlay">
        <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            dark
            eyebrow="The Hardware"
            title="From 500W prototype to 5kVA development unit."
            lead="Official photography from the BNM workshop in Waterloo, Sierra Leone."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {CFG_MEDIA.map((m, i) => (
              <Reveal key={m.src} delay={i * 0.08}>
                <figure className="group rounded-lg border border-white/10 bg-pine/60 overflow-hidden h-full">
                  <span className="block overflow-hidden">
                    <img
                      src={m.src}
                      alt={m.title}
                      loading="lazy"
                      width="1024"
                      height="768"
                      className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </span>
                  <figcaption className="p-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-volt/80">{m.tag}</p>
                    <p className="mt-2 text-sm font-semibold text-white">{m.title}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="cfg-video" className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <SectionHead
              eyebrow="Watch It Run"
              title="The Carbon Free Generator in operation."
              lead="Official footage from the BNM workshop — the Carbon Free Generator running. More demonstration footage will be published as testing progresses."
            />
            <Reveal delay={0.1}>
              <VideoCard />
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="cfg-benefits" className="bg-mist grid-lines-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead eyebrow="Key Benefits" title="Designed advantages of the BNM platform." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.05}>
                <article className="h-full rounded-lg border border-slate-200 bg-white p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-emerald-300">
                  <h3 className="font-display text-lg font-bold tracking-tight text-slate-900">{b.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{b.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="cfg-applications" className="relative overflow-hidden bg-obsidian noise-overlay">
        <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            dark
            eyebrow="Applications"
            title="Where the Carbon Free Generator is designed to serve."
            lead="These are potential application areas — the product is in development and not yet commercially deployed."
          />
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {CFG_APPLICATIONS.map((app, i) => {
              const Icon = APP_ICONS[i % APP_ICONS.length];
              return (
                <Reveal key={app} delay={i * 0.04}>
                  <div className="rounded-lg border border-white/10 bg-pine/60 p-6 h-full">
                    <Icon className="h-6 w-6 text-volt" aria-hidden="true" />
                    <p className="mt-4 text-sm font-semibold text-white">{app}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
              Potential applications — no commercial deployments have been announced
            </p>
          </Reveal>
        </div>
      </section>

      <section id="cfg-specs" data-testid="cfg-specs" className="bg-white border-b border-slate-200 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            eyebrow="Technical Specifications"
            title="What we can state today."
            lead="BNM publishes only verified specifications. Where engineering is still in progress, we say so."
          />
          <Reveal delay={0.1}>
            <div className="mt-12 overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full min-w-[560px] text-left">
                <caption className="sr-only">BNM Carbon Free Generator specifications</caption>
                <tbody>
                  {CFG_SPECS.map((row, i) => (
                    <tr key={row.label} className={i % 2 === 0 ? "bg-mist" : "bg-white"}>
                      <th scope="row" className="px-6 py-4 font-mono text-xs uppercase tracking-[0.14em] text-slate-500 font-medium w-1/3">
                        {row.label}
                      </th>
                      <td className={`px-6 py-4 text-sm ${row.value.includes("under development") ? "text-slate-400 italic" : "text-slate-900 font-medium"}`}>
                        {row.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>

          <div className="mt-16">
            <SectionHead eyebrow="Development Roadmap" title="From prototype to product." />
            <ol className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {ROADMAP.map((r, i) => (
                <Reveal as="li" key={r.stage} delay={i * 0.07}>
                  <div className={`h-full rounded-lg border p-6 ${r.active ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-mist"}`}>
                    <h3 className="font-display text-base font-bold tracking-tight text-slate-900">{r.stage}</h3>
                    <p className={`mt-2 font-mono text-[10px] uppercase tracking-[0.16em] ${r.active ? "text-emerald-700" : "text-slate-500"}`}>
                      {r.status}
                    </p>
                  </div>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section data-testid="cfg-faq" className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead eyebrow="FAQ" title="Questions about the Carbon Free Generator." />
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10 max-w-3xl">
              {CFG_FAQ.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-slate-200">
                  <AccordionTrigger data-testid={`cfg-faq-trigger-${i}`} className="text-left font-display font-semibold text-slate-900 hover:text-emerald-700">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Interested in the Carbon Free Generator?"
        text="For pilot partnerships, investment conversations or technical inquiries, contact the BNM team."
        primary={{ label: "Make an Inquiry", to: "/contact", testid: "cfg-contact-cta" }}
        secondary={{ label: "Partnership Opportunities", to: "/partners", testid: "cfg-partner-cta" }}
        testid="cfg-cta"
      />
    </>
  );
}
